import { ChevronDown } from "lucide-react";
import { useId } from "react";

import { useGetAdminOrderInitiators } from "@/common/api";
import { Button, Checkbox, Popover, PopoverContent, PopoverTrigger } from "@/common/components";

interface InitiatorFilterProps {
  value: string[];
  onChange: (companyIds: string[]) => void;
}

export function InitiatorFilter({ value, onChange }: InitiatorFilterProps) {
  const id = useId();
  const { data, isLoading, isError, refetch } = useGetAdminOrderInitiators({
    query: { staleTime: 60_000 },
  });
  const companies = data?.data.payload ?? [];
  const selectedNames = companies
    .filter((company) => company.id && value.includes(company.id))
    .map((company) => company.name);
  const selectionLabel =
    value.length === 0
      ? "Все инициаторы"
      : value.length === 1 && selectedNames[0]
        ? selectedNames[0]
        : `Выбрано: ${value.length}`;

  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-xs font-semibold text-Text-main leading-4 mb-1">
        Инициатор заявки
      </label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            className="w-full justify-between px-3 font-normal"
            title={selectedNames.join(", ") || "Все инициаторы"}
          >
            <span className="truncate">{selectionLabel}</span>
            <ChevronDown aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          aria-label="Инициаторы заявок"
          className="w-[var(--radix-popover-trigger-width)] min-w-64 max-w-[calc(100vw-2rem)] p-2"
        >
          {isLoading ? (
            <p className="p-2 text-sm text-muted-foreground" role="status">
              Загрузка компаний…
            </p>
          ) : isError ? (
            <div className="p-2 text-sm" role="alert">
              <p>Не удалось загрузить компании</p>
              <Button type="button" variant="link" onClick={() => void refetch()}>
                Повторить
              </Button>
            </div>
          ) : companies.length === 0 ? (
            <p className="p-2 text-sm text-muted-foreground">Нет инициаторов заявок</p>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-1">
              {companies.map((company) => {
                if (!company.id) return null;
                const companyId = company.id;
                return (
                  <label
                    key={companyId}
                    className="flex items-start gap-2 rounded p-2 cursor-pointer hover:bg-accent"
                  >
                    <Checkbox
                      className="mt-0.5"
                      checked={value.includes(companyId)}
                      onCheckedChange={(checked) =>
                        onChange(
                          checked === true
                            ? [...value, companyId]
                            : value.filter((selectedId) => selectedId !== companyId),
                        )
                      }
                    />
                    <span className="text-sm break-words min-w-0">{company.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
